// Display error notifications sequentially from the loading state message queue.
import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/joy/Button";
import Snackbar, { SnackbarCloseReason } from "@mui/joy/Snackbar";
import InfoIcon from '@mui/icons-material/Info';
import { MessageQueueItem, useLoadingState } from "../hooks/useLoading";

export interface ErrorStatusNotificationProps {
  autoHideDuration?: number,
  horizontal?: "left" | "center" | "right",
  vertical?: "top" | "bottom",
}

export function ErrorStatusNotification(props: ErrorStatusNotificationProps) {
  const {
    autoHideDuration = 6000,
    horizontal = "right",
    vertical = "bottom",
  } = props;
  const { messageQueue, popMessageQueue } = useLoadingState();
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<MessageQueueItem | undefined>();

  useEffect(() => {
    if (messageQueue.length) {
      const currentMsg = messageQueue[0];
      if (!currentMsg.message) {
        currentMsg.message = "An unknown error occurred.";
      }
      setMessageInfo(currentMsg);
      setOpen(true);
    }
  }, [messageQueue]);

  const handleClose = useCallback((e: React.SyntheticEvent | Event | null, reason?: SnackbarCloseReason) => {
    if (reason === "clickaway") {
      return;
    }
    popMessageQueue();
    setOpen(false);
  }, [popMessageQueue]);

  return (
    <Snackbar
      anchorOrigin={{ vertical, horizontal }}
      autoHideDuration={autoHideDuration}
      color={messageInfo?.type === "error" ? "danger" : "primary"}
      key={messageInfo?.key}
      open={open}
      onClose={handleClose}
      size="lg"
      startDecorator={messageInfo?.type === "error" ? "😮" :<InfoIcon/>}
      endDecorator={
        <Button
          color={messageInfo?.type === "error" ? "danger" : "primary"}
          onClick={handleClose}
          size="sm"
          variant="soft"
        >
          Dismiss
        </Button>
      }
      variant="solid"
    >
      {messageInfo?.message}
    </Snackbar>
  );
}
